import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ContactErrors, ContactField, ContactPayload } from '@/lib/contact'

import Link from 'next/link'
import { useState } from 'react'

import {
    ChevronDown,
    CircleCheck,
    LoaderCircle,
    Send,
    TriangleAlert
} from 'lucide-react'

import { cn } from '@/lib/utils'

import {
    CONTACT_FIELD_ORDER,
    CONTACT_LIMITS,
    EMPTY_CONTACT,
    normalizeContact,
    validateContact
} from '@/lib/contact'

import { company } from '@/constants/company'
import { PROJECT_TYPES } from '@/constants/content'

type Status = 'idle' | 'submitting' | 'success' | 'error'

interface FieldProps {
    id: ContactField
    label: string
    optional?: boolean
    error?: string
    hint?: string
    className?: string
    children: ReactNode
}

const FALLBACK_ERROR = `Ihre Anfrage konnte gerade nicht gesendet werden. Bitte versuchen Sie es erneut oder rufen Sie uns an: ${company.phone}.`

const inputClass = 'block w-full rounded-xl border border-input bg-card px-4 py-3 text-[0.95rem] text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground/70 focus:border-primary focus:ring-4 focus:ring-primary/15 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus:ring-destructive/15'

const fieldId = (field: ContactField): string => `contact-${field}`

const Field: FC<FieldProps> = ({
    id,
    label,
    optional = false,
    error,
    hint,
    className,
    children
}): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={fieldId(id)} className='mb-2 flex items-baseline justify-between gap-3 text-sm font-medium text-foreground'>
                <span>{label}</span>

                {optional && (
                    <span className='text-xs font-normal text-muted-foreground'>
                        optional
                    </span>
                )}
            </label>

            {children}

            {hint && !error && (
                <p id={`${fieldId(id)}-hint`} className='mt-1.5 text-xs text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${fieldId(id)}-error`} className='mt-1.5 text-sm text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<ContactPayload>(EMPTY_CONTACT)
    const [errors, setErrors] = useState<ContactErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const update = (field: keyof ContactPayload) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const target = event.target
        const value = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target.value

        setValues((current) => ({ ...current, [field]: value }))

        if (field !== 'website' && errors[field]) {
            setErrors((current) => ({ ...current, [field]: undefined }))
        }
    }

    const describedBy = (field: ContactField, withHint = false): string | undefined => {
        if (errors[field]) return `${fieldId(field)}-error`
        if (withHint) return `${fieldId(field)}-hint`

        return undefined
    }

    const focusFirstError = (nextErrors: ContactErrors) => {
        const first = CONTACT_FIELD_ORDER.find((field) => nextErrors[field])

        if (first) document.getElementById(fieldId(first))?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const payload = normalizeContact(values)
        const nextErrors = validateContact(payload)

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length > 0) {
            focusFirstError(nextErrors)
            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })

            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: ContactErrors } | null

            if (response.ok && data?.ok) {
                setValues(EMPTY_CONTACT)
                setErrors({})
                setStatus('success')
                return
            }

            if (data?.errors) {
                setErrors(data.errors)
                focusFirstError(data.errors)
            }

            setMessage(data?.message ?? FALLBACK_ERROR)
            setStatus('error')
        }
        catch {
            setMessage(FALLBACK_ERROR)
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex flex-col items-start py-6 sm:py-10'>
                <span className='flex size-14 items-center justify-center rounded-2xl bg-accent text-accent-foreground'>
                    <CircleCheck className='size-6' aria-hidden='true' />
                </span>

                <h3 className='mt-6 text-2xl font-light tracking-[-0.015em] text-foreground'>
                    Vielen Dank, Ihre Anfrage ist eingegangen.
                </h3>

                <p className='mt-3 max-w-md text-pretty leading-relaxed text-muted-foreground'>
                    Wir melden uns zeitnah persönlich bei Ihnen. Steht die Betonage schon in den nächsten Tagen an, erreichen Sie uns direkt unter{' '}
                    <a href={company.phoneHref} className='font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-primary'>
                        {company.phone}
                    </a>
                    .
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-8 inline-flex h-11 items-center rounded-full border border-border px-5 text-[0.95rem] font-medium text-foreground transition-colors hover:bg-muted'
                >
                    Weitere Anfrage senden
                </button>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form onSubmit={handleSubmit} noValidate aria-describedby='contact-form-note'>
            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id='name' label='Name' error={errors.name}>
                    <input
                        id={fieldId('name')}
                        name='name'
                        type='text'
                        autoComplete='name'
                        required
                        maxLength={CONTACT_LIMITS.name}
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={describedBy('name')}
                        className={inputClass}
                    />
                </Field>

                <Field id='company' label='Unternehmen' optional error={errors.company}>
                    <input
                        id={fieldId('company')}
                        name='company'
                        type='text'
                        autoComplete='organization'
                        maxLength={CONTACT_LIMITS.company}
                        value={values.company}
                        onChange={update('company')}
                        aria-invalid={errors.company ? true : undefined}
                        aria-describedby={describedBy('company')}
                        className={inputClass}
                    />
                </Field>

                <Field id='email' label='E-Mail' error={errors.email}>
                    <input
                        id={fieldId('email')}
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        maxLength={CONTACT_LIMITS.email}
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={describedBy('email')}
                        className={inputClass}
                    />
                </Field>

                <Field id='phone' label='Telefon' optional error={errors.phone}>
                    <input
                        id={fieldId('phone')}
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        maxLength={CONTACT_LIMITS.phone}
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={errors.phone ? true : undefined}
                        aria-describedby={describedBy('phone')}
                        className={inputClass}
                    />
                </Field>

                <Field id='projectType' label='Bauteil oder Projektart' optional error={errors.projectType}>
                    <div className='relative'>
                        <select
                            id={fieldId('projectType')}
                            name='projectType'
                            value={values.projectType}
                            onChange={update('projectType')}
                            aria-invalid={errors.projectType ? true : undefined}
                            aria-describedby={describedBy('projectType')}
                            className={cn(inputClass, 'appearance-none pr-11', !values.projectType && 'text-muted-foreground')}
                        >
                            <option value=''>Bitte auswählen</option>

                            {PROJECT_TYPES.map((type) => (
                                <option key={type} value={type} className='text-foreground'>
                                    {type}
                                </option>
                            ))}
                        </select>

                        <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    </div>
                </Field>

                <Field id='pourDate' label='Geplanter Betoniertermin' optional error={errors.pourDate} hint='Zum Beispiel KW 48 oder Mitte Dezember'>
                    <input
                        id={fieldId('pourDate')}
                        name='pourDate'
                        type='text'
                        maxLength={CONTACT_LIMITS.pourDate}
                        value={values.pourDate}
                        onChange={update('pourDate')}
                        aria-invalid={errors.pourDate ? true : undefined}
                        aria-describedby={describedBy('pourDate', true)}
                        className={inputClass}
                    />
                </Field>

                <Field id='message' label='Ihr Vorhaben' error={errors.message} hint='Bauteil, Dicke, Anzahl der Abschnitte, Ort: alles, was Sie schon wissen.' className='sm:col-span-2'>
                    <textarea
                        id={fieldId('message')}
                        name='message'
                        required
                        rows={5}
                        maxLength={CONTACT_LIMITS.message}
                        value={values.message}
                        onChange={update('message')}
                        aria-invalid={errors.message ? true : undefined}
                        aria-describedby={describedBy('message', true)}
                        className={cn(inputClass, 'min-h-36 resize-y leading-relaxed')}
                    />
                </Field>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='contact-website'>Website</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            <div className='mt-6'>
                <div className='flex gap-3'>
                    <input
                        id={fieldId('privacy')}
                        name='privacy'
                        type='checkbox'
                        checked={values.privacy}
                        onChange={update('privacy')}
                        aria-invalid={errors.privacy ? true : undefined}
                        aria-describedby={describedBy('privacy')}
                        className='mt-0.5 size-5 shrink-0 cursor-pointer rounded-md border-input accent-primary'
                    />

                    <label htmlFor={fieldId('privacy')} className='cursor-pointer text-pretty text-sm leading-relaxed text-muted-foreground'>
                        Ich habe die{' '}
                        <Link href='/datenschutz' target='_blank' className='font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-primary'>
                            Datenschutzhinweise
                        </Link>{' '}
                        gelesen. Meine Angaben werden zur Bearbeitung der Anfrage gespeichert.
                    </label>
                </div>

                {errors.privacy && (
                    <p id={`${fieldId('privacy')}-error`} className='mt-1.5 pl-8 text-sm text-destructive'>
                        {errors.privacy}
                    </p>
                )}
            </div>

            {status === 'error' && message && (
                <div role='alert' className='mt-6 flex gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 p-4 text-sm leading-relaxed text-foreground'>
                    <TriangleAlert className='mt-0.5 size-4 shrink-0 text-destructive' aria-hidden='true' />
                    {message}
                </div>
            )}

            <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p id='contact-form-note' className='text-sm text-muted-foreground'>
                    Unverbindlich und kostenlos. Wir melden uns persönlich.
                </p>

                <button
                    type='submit'
                    disabled={submitting}
                    className='inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-7 font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-wait disabled:opacity-80'
                >
                    {submitting
                        ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                        : <Send className='size-4' aria-hidden='true' />}
                    {submitting ? 'Wird gesendet …' : 'Anfrage senden'}
                </button>
            </div>
        </form>
    )
}
